/**
 * Progressive editorial media: a small rendition of the same Shopify image is
 * painted beneath the final responsive asset. The final asset is never blurred
 * or scaled, so its crop cannot shift as it resolves.
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @param {Document | Element} root */
function enableProgressiveMedia(root = document) {
  if (reducedMotion.matches) return;

  /** @type {NodeListOf<HTMLElement>} */
  const media = /** @type {NodeListOf<HTMLElement>} */ (root.querySelectorAll('[data-progressive-media]'));
  if (!media.length) return;

  document.documentElement.classList.add('image-reveal-enhanced');
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const container = /** @type {HTMLElement} */ (entry.target);
      const image = /** @type {HTMLImageElement | null} */ (container.querySelector('[data-progressive-image]'));
      const placeholder = container.dataset.progressiveMedia;
      observer.unobserve(container);
      if (!image || !placeholder) return;

      container.style.setProperty('--progressive-image', `url("${placeholder}")`);
      container.classList.add('is-progressive');

      const reveal = () => window.requestAnimationFrame(() => container.classList.add('is-loaded'));
      const decodeAndReveal = () => {
        image.decode().catch(() => undefined).then(reveal);
      };

      if (image.complete && image.naturalWidth) {
        decodeAndReveal();
      } else {
        image.addEventListener('load', decodeAndReveal, { once: true });
        image.addEventListener('error', reveal, { once: true });
      }
    });
  }, { rootMargin: '320px 0px', threshold: 0.01 });

  media.forEach((container) => observer.observe(container));
}

enableProgressiveMedia();

document.addEventListener('shopify:section:load', (event) => enableProgressiveMedia(/** @type {Element} */ (event.target)));

export {};
